import { idadeEmMeses, rankearSemelhantes, type Classe } from '@equiview/domain';
import { traitsV1, type TraitGroup } from '@equiview/protocol';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router';
import { BuscaAnimal } from '../components/BuscaAnimal.js';
import { FotoDaMidia } from '../components/FotoDaMidia.js';
import { PerfilDiferencas, type LinhaPerfil } from '../components/PerfilDiferencas.js';
import {
  Abas,
  Aguardar,
  Alternador,
  Botao,
  CabecalhoPagina,
  classeTd,
  classeTh,
  Etiqueta,
  Indicador,
  Painel,
  Seletor,
  Tabela,
} from '../components/ui/index.js';
import {
  classeNaData,
  faixaNaData,
  formatarData,
  formatarIdade,
  formatarValor,
  indicesDaFicha,
  ROTULO_CLASSE,
  ROTULO_CLASSE_PLURAL,
  ROTULO_FAIXA,
  ROTULO_GRUPO,
  ROTULO_INDICE,
  traitPorCodigo,
  unidadeCurta,
  valoresDaFicha,
} from '../dados/catalogo.js';
import { useConsulta, usePodeCadastrar } from '../dados/contexto.js';
import type { Animal, Avaliacao, Medida, Midia } from '../dados/fonte.js';

/**
 * Variaveis da comparacao: medidas continuas (alturas, comprimentos, larguras,
 * perimetros, angulos) e os indices. Aprumo e escore ordinal e fica de fora.
 * Metodo provisorio: os indices zootecnicos finais ainda serao definidos.
 */
const MEDIDAS_CONTINUAS = traitsV1
  .filter((t) => t.kind === 'linear' || t.kind === 'perimetro' || t.kind === 'angulo')
  .map((t) => t.code);
const INDICES = Object.keys(ROTULO_INDICE) as Array<keyof typeof ROTULO_INDICE>;
const chaveIndice = (k: string) => `IDX_${k}`;
const VARIAVEIS = [...MEDIDAS_CONTINUAS, ...INDICES.map(chaveIndice)];

type Grupo = 'todas' | Extract<TraitGroup, 'altura' | 'comprimento' | 'largura' | 'perimetro' | 'angulo'> | 'indices';
const GRUPOS: Array<{ valor: Grupo; rotulo: string }> = [
  { valor: 'todas', rotulo: 'Todas' },
  { valor: 'altura', rotulo: ROTULO_GRUPO.altura },
  { valor: 'comprimento', rotulo: ROTULO_GRUPO.comprimento },
  { valor: 'largura', rotulo: ROTULO_GRUPO.largura },
  { valor: 'perimetro', rotulo: ROTULO_GRUPO.perimetro },
  { valor: 'angulo', rotulo: ROTULO_GRUPO.angulo },
  { valor: 'indices', rotulo: 'Índices' },
];
const grupoDe = (v: string): Grupo => (v.startsWith('IDX_') ? 'indices' : ((traitPorCodigo.get(v)?.group as Grupo) ?? 'todas'));

const MAXIMO_SUGESTOES = 10;
const DESTAQUES = 5;
const NO_PERFIL = 15;

interface Ficha {
  avaliacao: Avaliacao;
  animal: Animal;
  classe: Classe;
  grupo: string;
  valores: Record<string, number | null>;
}

export function Comparar() {
  const consulta = useConsulta(async (f) => {
    const [animais, avaliacoes, capas] = await Promise.all([f.listarAnimais(), f.avaliacoes(), f.capas()]);
    const medidas = await f.medidas(avaliacoes.map((a) => a.id));
    return { animais, avaliacoes, medidas, capas };
  }, []);
  return <Aguardar consulta={consulta}>{(d) => <Comparacao {...d} />}</Aguardar>;
}

function montarFichas(animais: Animal[], avaliacoes: Avaliacao[], medidas: Medida[]): Map<string, Ficha> {
  const porAvaliacao = new Map<string, Medida[]>();
  for (const m of medidas) porAvaliacao.set(m.assessment_id, [...(porAvaliacao.get(m.assessment_id) ?? []), m]);
  const fichas = new Map<string, Ficha>();
  for (const av of avaliacoes) {
    const animal = animais.find((a) => a.id === av.animal_id);
    if (!animal) continue;
    const valores = valoresDaFicha(porAvaliacao.get(av.id) ?? []);
    const ind = indicesDaFicha(valores);
    for (const k of INDICES) valores[chaveIndice(k)] = ind[k];
    const classe = classeNaData(animal, animais, av.visit_date);
    // Potro so se compara com potro da mesma faixa: um potro de 8 meses nao e
    // "parecido" com um de 30 so por serem potros.
    const grupo = classe === 'potro' ? `potro:${faixaNaData(animal, av.visit_date)}` : classe;
    fichas.set(av.id, { avaliacao: av, animal, classe, grupo, valores });
  }
  return fichas;
}

/** Desvio-padrao de cada variavel na populacao de fichas: a regua das diferencas padronizadas. */
function desvios(fichas: Iterable<Ficha>): Map<string, number> {
  const lista = [...fichas];
  const r = new Map<string, number>();
  for (const v of VARIAVEIS) {
    const xs = lista.map((f) => f.valores[v]).filter((x): x is number => x != null);
    if (xs.length < 2) continue;
    const m = xs.reduce((a, b) => a + b, 0) / xs.length;
    const dp = Math.sqrt(xs.reduce((a, b) => a + (b - m) ** 2, 0) / xs.length);
    if (dp > 0) r.set(v, dp);
  }
  return r;
}

const rotuloVariavel = (v: string) =>
  v.startsWith('IDX_') ? `Índice ${ROTULO_INDICE[v.slice(4) as keyof typeof ROTULO_INDICE].toLowerCase()}` : (traitPorCodigo.get(v)?.label ?? v);

function formatarVariavel(v: string, n: number, sinal = false): string {
  const s = sinal && n > 0 ? '+' : '';
  if (v.startsWith('IDX_')) return `${s}${n.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}`;
  const t = traitPorCodigo.get(v);
  return `${s}${formatarValor(n, t)}${unidadeCurta(t)}`;
}

const idadeNaFicha = (f: Ficha) => formatarIdade(idadeEmMeses(f.animal.birth_date, f.avaliacao.visit_date));

function Comparacao({
  animais,
  avaliacoes,
  medidas,
  capas,
}: {
  animais: Animal[];
  avaliacoes: Avaliacao[];
  medidas: Medida[];
  capas: Map<string, Midia>;
}) {
  const [params, setParams] = useSearchParams();
  const mudar = (novos: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(novos)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };

  const fichas = useMemo(() => montarFichas(animais, avaliacoes, medidas), [animais, avaliacoes, medidas]);
  const dp = useMemo(() => desvios(fichas.values()), [fichas]);
  const fichasDe = (animalId: string) => avaliacoes.filter((a) => a.animal_id === animalId);

  /** A ficha pedida na URL, se for do animal; senao a mais recente dele. */
  function fichaEscolhida(animalId: string, fichaId: string | null): Ficha | undefined {
    const f = fichaId ? fichas.get(fichaId) : undefined;
    return f?.animal.id === animalId ? f : fichas.get(fichasDe(animalId).at(-1)?.id ?? '');
  }

  const animalA = animais.find((a) => a.id === params.get('a')) ?? null;
  const fichaA = animalA ? fichaEscolhida(animalA.id, params.get('fa')) : undefined;
  const animalB = animais.find((a) => a.id === params.get('b') && a.id !== animalA?.id) ?? null;
  const fichaB = animalB ? fichaEscolhida(animalB.id, params.get('fb')) : undefined;
  const modo = params.get('modo') === 'divergentes' ? 'divergentes' : 'semelhantes';
  const todosOsGrupos = params.get('grupo') === 'todos';
  /*
    Onde procurar os semelhantes: no mesmo haras do animal A, por padrao. "Toda
    a base" so para a equipe — o dono do haras so enxerga o proprio plantel, e a
    regra final (haras ou base toda, e o que o `data_sharing_level` de cada haras
    permite) e pergunta em aberto ao Rafael.
  */
  const equipe = usePodeCadastrar();
  const todaABase = equipe && params.get('escopo') === 'base';

  const ranking = useMemo(() => {
    if (!fichaA) return [];
    const candidatos = [...fichas.values()]
      .filter(
        (f) =>
          f.animal.id !== fichaA.animal.id &&
          (todosOsGrupos || f.grupo === fichaA.grupo) &&
          (todaABase || f.animal.owner_farm_id === fichaA.animal.owner_farm_id),
      )
      .map((f) => ({ item: f, valores: f.valores }));
    const ordenado = rankearSemelhantes(fichaA.valores, candidatos, VARIAVEIS);
    if (modo === 'divergentes') ordenado.reverse();
    // Um animal aparece uma vez so, com a ficha dele que mais se encaixa no modo.
    const vistos = new Set<string>();
    return ordenado
      .filter((r) => !vistos.has(r.item.animal.id) && vistos.add(r.item.animal.id))
      .slice(0, MAXIMO_SUGESTOES);
  }, [fichas, fichaA, modo, todosOsGrupos, todaABase]);

  const avaliado = (a: Animal) => fichasDe(a.id).length > 0;

  const escolhaDeFicha = (animal: Animal, atual: Ficha | undefined, chave: 'fa' | 'fb') => (
    <div className="space-y-1.5">
      <span className="text-xs text-apagado">Ficha usada</span>
      {/* Quebra de linha, e nao rolagem: em duas colunas a rolagem invadiria o outro lado. */}
      <Seletor
        rotulo="Ficha usada"
        valor={atual?.avaliacao.id}
        onMudar={(id) => mudar({ [chave]: id })}
        opcoes={fichasDe(animal.id).map((av) => ({
          valor: av.id,
          conteudo: (
            <>
              {formatarIdade(idadeEmMeses(animal.birth_date, av.visit_date))}
              <span className="numero ml-1.5 opacity-75">{formatarData(av.visit_date)}</span>
            </>
          ),
        }))}
      />
    </div>
  );

  return (
    <div className="space-y-6">
      <CabecalhoPagina
        trilha={[{ rotulo: 'Plantel', para: '/animais' }, { rotulo: 'Comparar' }]}
        sobretitulo="Análise comparativa"
        titulo="Comparar"
        meta={<span>Pesquise dois animais, ou escolha o primeiro e veja os mais semelhantes ou os mais diferentes dele.</span>}
      />

      <Painel corpo="p-0">
        <div className="grid divide-y divide-borda md:grid-cols-2 md:divide-x md:divide-y-0">
          {(
            [
              { letra: 'A', animal: animalA, ficha: fichaA, chave: 'a', chaveFicha: 'fa', outro: animalB },
              { letra: 'B', animal: animalB, ficha: fichaB, chave: 'b', chaveFicha: 'fb', outro: animalA },
            ] as const
          ).map((s) => (
            <div key={s.letra} className="space-y-3 p-4">
              <div className="flex items-center gap-2">
                <MarcaLado letra={s.letra} />
                <span className="rotulo-secao">Animal {s.letra}</span>
              </div>
              <BuscaAnimal
                rotulo={`Animal ${s.letra}`}
                animais={animais}
                valor={s.animal}
                excluir={s.outro?.id ?? null}
                apenas={avaliado}
                ocultarRotulo
                onEscolher={(a) => mudar({ [s.chave]: a?.id ?? null, [s.chaveFicha]: null })}
              />
              {s.animal && escolhaDeFicha(s.animal, s.ficha, s.chaveFicha)}
            </div>
          ))}
        </div>
      </Painel>

      {fichaA && fichaB && <Resultado a={fichaA} b={fichaB} fichas={fichas} dp={dp} capas={capas} />}

      {fichaA ? (
        <Painel
          titulo={`Sugestões para ${fichaA.animal.name}`}
          corpo="p-0"
          acoes={
            <>
              {equipe && (
                <Alternador
                  rotulo="Onde procurar"
                  valor={todaABase ? 'base' : 'haras'}
                  onMudar={(v) => mudar({ escopo: v === 'base' ? 'base' : null })}
                  opcoes={[
                    { valor: 'haras', titulo: 'Só o haras do animal', rotulo: 'Neste haras' },
                    { valor: 'base', titulo: 'Todos os haras da base', rotulo: 'Toda a base' },
                  ]}
                />
              )}
              <Alternador
                rotulo="Grupo de comparação"
                valor={todosOsGrupos ? 'todos' : 'mesmo'}
                onMudar={(v) => mudar({ grupo: v === 'todos' ? 'todos' : null })}
                opcoes={[
                  { valor: 'mesmo', titulo: 'Só o mesmo grupo', rotulo: `Só ${rotuloGrupo(fichaA)}` },
                  { valor: 'todos', titulo: 'Todas as classes', rotulo: 'Todas as classes' },
                ]}
              />
            </>
          }
        >
          <div className="px-4 pt-1">
            <Abas
              rotulo="Tipo de sugestão"
              tamanho="sm"
              valor={modo}
              onMudar={(v) => mudar({ modo: v === 'divergentes' ? 'divergentes' : null })}
              abas={[
                { valor: 'semelhantes', rotulo: 'Mais semelhantes' },
                { valor: 'divergentes', rotulo: 'Mais diferentes' },
              ]}
            />
          </div>
          {ranking.length === 0 ? (
            <p className="px-4 py-5 text-sm text-apagado">
              Nenhum outro animal avaliado nesse grupo. Experimente “Todas as classes”.
            </p>
          ) : (
            <Tabela rotulo="Sugestões">
              <thead>
                <tr>
                  <th scope="col" className={`${classeTh} w-10 text-right`}>
                    #
                  </th>
                  <th scope="col" className={classeTh}>
                    Animal
                  </th>
                  <th scope="col" className={`${classeTh} hidden sm:table-cell`}>
                    Classe
                  </th>
                  <th scope="col" className={`${classeTh} hidden md:table-cell`}>
                    Ficha
                  </th>
                  <th scope="col" className={`${classeTh} w-48`}>
                    Distância
                  </th>
                  <th scope="col" className={classeTh}>
                    <span className="sr-only">Ação</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {ranking.map((r, i) => {
                  const f = r.item;
                  const ativo = fichaB?.avaliacao.id === f.avaliacao.id;
                  const maior = Math.max(...ranking.map((x) => x.distancia), 1e-9);
                  return (
                    <tr key={f.avaliacao.id} className={ativo ? 'bg-destaque-fundo' : ''}>
                      <td className={`${classeTd} numero text-right text-apagado`}>{i + 1}</td>
                      <td className={classeTd}>
                        <span className="font-serif font-semibold text-marca">{f.animal.name}</span>
                      </td>
                      <td className={`${classeTd} hidden sm:table-cell`}>
                        <Etiqueta>{ROTULO_CLASSE[f.classe]}</Etiqueta>
                      </td>
                      <td className={`${classeTd} numero hidden text-apagado md:table-cell`}>aos {idadeNaFicha(f)}</td>
                      <td className={classeTd}>
                        <span className="flex items-center gap-2">
                          <span className="h-1.5 flex-1 bg-borda" aria-hidden>
                            <span className="block h-full bg-destaque" style={{ width: `${(r.distancia / maior) * 100}%` }} />
                          </span>
                          <span className="numero w-10 text-right text-[13px]">
                            {r.distancia.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </span>
                      </td>
                      <td className={`${classeTd} text-right`}>
                        <Botao
                          tamanho="sm"
                          variante={ativo ? 'principal' : 'secundario'}
                          // A sugestao preenche o Animal B, com a ficha que mais se encaixou.
                          onClick={() => mudar({ b: f.animal.id, fb: f.avaliacao.id })}
                        >
                          {ativo ? 'Em B' : 'Usar como B'}
                        </Botao>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          )}
          <p className="border-t border-borda bg-superficie-2 px-4 py-3 text-xs text-apagado">
            Método provisório: cada medida é padronizada pelo desvio do plantel e a distância é a média entre elas
            (menor = mais parecido). Os índices zootécnicos finais ainda serão definidos com o Rafael.
          </p>
        </Painel>
      ) : (
        <Painel>
          <p className="text-sm text-apagado">Pesquise o Animal A para ver sugestões de animais semelhantes e diferentes.</p>
        </Painel>
      )}
    </div>
  );
}

function MarcaLado({ letra }: { letra: 'A' | 'B' }) {
  return (
    <span
      aria-hidden
      className={`inline-flex size-5 items-center justify-center text-xs font-bold ${
        letra === 'A' ? 'bg-marca text-sobre-marca' : 'bg-destaque text-gelo'
      }`}
    >
      {letra}
    </span>
  );
}

function rotuloGrupo(f: Ficha): string {
  if (f.classe !== 'potro') return ROTULO_CLASSE_PLURAL[f.classe].toLowerCase();
  const faixa = faixaNaData(f.animal, f.avaliacao.visit_date);
  return faixa ? `potros ${ROTULO_FAIXA[faixa]}` : 'potros';
}

function Resultado({
  a,
  b,
  fichas,
  dp,
  capas,
}: {
  a: Ficha;
  b: Ficha;
  fichas: Map<string, Ficha>;
  dp: Map<string, number>;
  capas: Map<string, Midia>;
}) {
  const [grupo, setGrupo] = useState<Grupo>('todas');
  const [soMaiores, setSoMaiores] = useState(false);

  const linhas = VARIAVEIS.flatMap((v) => {
    const va = a.valores[v];
    const vb = b.valores[v];
    const desvio = dp.get(v);
    if (va == null || vb == null || !desvio) return [];
    const media = (Math.abs(va) + Math.abs(vb)) / 2 || 1;
    return [{ v, va, vb, delta: vb - va, pct: ((vb - va) / Math.abs(va || media)) * 100, z: (vb - va) / desvio }];
  });
  const porTamanho = [...linhas].sort((x, y) => Math.abs(y.z) - Math.abs(x.z));
  const maiores = new Set(porTamanho.slice(0, DESTAQUES).map((l) => l.v));

  // Distancia da dupla na mesma regua das sugestoes (padronizada pelo plantel).
  const distancia = rankearSemelhantes(a.valores, [{ item: b, valores: b.valores }, ...[...fichas.values()].filter((f) => f !== b && f !== a).map((f) => ({ item: f, valores: f.valores }))], VARIAVEIS).find(
    (r) => r.item === b,
  );

  const perfil: LinhaPerfil[] = porTamanho.slice(0, NO_PERFIL).map((l) => ({
    chave: l.v,
    rotulo: rotuloVariavel(l.v),
    z: l.z,
    textoA: formatarVariavel(l.v, l.va),
    textoB: formatarVariavel(l.v, l.vb),
    textoDelta: formatarVariavel(l.v, l.delta, true),
  }));

  const visiveis = linhas
    .filter((l) => grupo === 'todas' || grupoDe(l.v) === grupo)
    .filter((l) => !soMaiores || maiores.has(l.v) || porTamanho.indexOf(l) < 10);

  const nomeCurto = (f: Ficha) => f.animal.name.split(' ')[0] ?? f.animal.name;

  return (
    <section aria-label="Resultado da comparação" className="space-y-6">
      {/* Lado a lado, na mesma vista: o olho confirma (ou questiona) o que os numeros dizem. */}
      <div className="grid grid-cols-2 gap-3">
        {[a, b].map((f, i) => (
          <figure key={f.animal.id} className="overflow-hidden rounded-(--radius-painel) border border-borda">
            <FotoDaMidia
              midia={capas.get(f.animal.id)}
              alt={`${f.animal.name}, perfil esquerdo`}
              className="aspect-4/3 w-full"
              legenda="Sem foto de perfil"
            />
            <figcaption className="px-3 py-2 text-sm">
              <span className="font-mono text-xs text-apagado">{i === 0 ? 'A' : 'B'}</span>{' '}
              <span className="font-serif font-semibold text-marca">{f.animal.name}</span>
            </figcaption>
          </figure>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-(--radius-painel) border border-borda bg-borda lg:grid-cols-4">
        <Indicador
          rotulo="Distância geral"
          valor={distancia ? distancia.distancia.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : '—'}
          complemento="desvios-padrão · menor = mais parecido"
        />
        <Indicador rotulo="Variáveis comparadas" valor={linhas.length} complemento="medidas contínuas e índices" />
        <div className="col-span-2 min-w-0 border-l-2 border-destaque bg-superficie px-4 py-3">
          <p className="rotulo-secao">Maiores diferenças</p>
          <ol className="mt-1.5 space-y-0.5 text-sm">
            {porTamanho.slice(0, 3).map((l) => (
              <li key={l.v} className="flex justify-between gap-3">
                <span className="truncate">{rotuloVariavel(l.v)}</span>
                <span className="numero shrink-0 text-apagado">
                  {l.delta > 0 ? `${nomeCurto(b)} maior` : `${nomeCurto(a)} maior`} · {formatarVariavel(l.v, Math.abs(l.delta))}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>

      <div className="grid items-start gap-6 xl:grid-cols-[minmax(0,5fr)_minmax(0,6fr)]">
        <Painel titulo={`Perfil de diferenças · ${NO_PERFIL} maiores`}>
          <PerfilDiferencas linhas={perfil} rotuloA={`A · ${nomeCurto(a)}`} rotuloB={`B · ${nomeCurto(b)}`} />
        </Painel>

        <Painel
          titulo="Lado a lado"
          corpo="p-0"
          acoes={
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={soMaiores}
                onChange={(e) => setSoMaiores(e.target.checked)}
                className="size-4 min-h-0 accent-(--color-marca)"
              />
              Só as maiores diferenças
            </label>
          }
        >
          <div className="px-4 pt-1">
            <Abas rotulo="Grupo de medidas" tamanho="sm" valor={grupo} onMudar={setGrupo} abas={GRUPOS} />
          </div>
          <div className="max-h-[70vh] overflow-auto">
            <Tabela rotulo="Comparação lado a lado">
              <thead className="sticky top-0 z-10">
                <tr>
                  <th scope="col" className={classeTh}>
                    Medida
                  </th>
                  <ColunaAnimal letra="A" ficha={a} />
                  <ColunaAnimal letra="B" ficha={b} />
                  <th scope="col" className={`${classeTh} text-right`}>
                    Δ (B−A)
                  </th>
                  <th scope="col" className={`${classeTh} hidden text-right sm:table-cell`}>
                    Δ%
                  </th>
                </tr>
              </thead>
              <tbody>
                {visiveis.map((l) => {
                  const destaque = maiores.has(l.v);
                  return (
                    <tr key={l.v}>
                      <th
                        scope="row"
                        className={`${classeTd} border-l-2 text-left ${destaque ? 'border-l-destaque font-semibold' : 'border-l-transparent font-normal'}`}
                      >
                        {rotuloVariavel(l.v)}
                      </th>
                      <td className={`${classeTd} numero text-right whitespace-nowrap`}>{formatarVariavel(l.v, l.va)}</td>
                      <td className={`${classeTd} numero text-right whitespace-nowrap`}>{formatarVariavel(l.v, l.vb)}</td>
                      <td className={`${classeTd} numero text-right whitespace-nowrap ${destaque ? 'font-semibold' : 'text-apagado'}`}>
                        {formatarVariavel(l.v, l.delta, true)}
                      </td>
                      <td className={`${classeTd} numero hidden text-right whitespace-nowrap text-apagado sm:table-cell`}>
                        {`${l.pct > 0 ? '+' : ''}${l.pct.toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Tabela>
          </div>
          <p className="border-t border-borda px-4 py-2 text-xs text-apagado">
            As {DESTAQUES} medidas que mais se afastam (em desvios-padrão do plantel) têm o filete à esquerda.
          </p>
        </Painel>
      </div>
    </section>
  );
}

function ColunaAnimal({ letra, ficha }: { letra: 'A' | 'B'; ficha: Ficha }) {
  return (
    <th
      scope="col"
      // Sem classeTh: esta coluna tem duas linhas (nome e idade) e nao vai em maiusculas.
      className="border-b border-borda-forte bg-superficie-2 px-3 py-1.5 text-right text-xs whitespace-nowrap text-apagado"
    >
      <span className="inline-flex items-center gap-1.5">
        <MarcaLado letra={letra} />
        <Link
          to={`/animais/${ficha.animal.id}?ficha=${ficha.avaliacao.id}`}
          className="max-w-32 truncate text-[13px] font-semibold tracking-normal text-destaque-texto hover:underline"
        >
          {ficha.animal.name.split(' ')[0]}
        </Link>
      </span>
      <span className="block text-xs font-normal tracking-normal">aos {idadeNaFicha(ficha)}</span>
    </th>
  );
}
